import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { requestService } from "@/services/ServiceRegistry";
import { CreateRequest, UpdateRequest, RequestFilters } from "@/types/request";
import { reservationKeys } from "@/hooks/useReservations";
import { settlementKeys } from "@/components/pages/settlement/hooks/useSettlement";
import { billingTransactionKeys } from "@/hooks/useBillingTransactions";

export const requestKeys = {
  all: ["requests"] as const,
  lists: () => [...requestKeys.all, "list"] as const,
  list: (filters?: RequestFilters) =>
    [...requestKeys.lists(), filters] as const,
  details: () => [...requestKeys.all, "detail"] as const,
  detail: (id: string) => [...requestKeys.details(), id] as const,
  listCreators: () => [...requestKeys.all, "listCreators" as const],
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateRequestRelatedQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: requestKeys.all });
  queryClient.invalidateQueries({ queryKey: reservationKeys.all });
  queryClient.invalidateQueries({ queryKey: settlementKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useRequests = (filters?: RequestFilters, enabled?: boolean) => {
  return useQuery({
    queryKey: requestKeys.list(filters),
    queryFn: () => requestService.getRequests(filters),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

export const useRequestsCreators = () => {
  return useQuery({
    queryKey: requestKeys.listCreators(),
    queryFn: () => requestService.getRequestsCreators(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRequest = (id: string, enabled = true) => {
  return useQuery({
    queryKey: requestKeys.detail(id),
    queryFn: () => requestService.getRequestById(id),
    enabled: !!id && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestData: CreateRequest) =>
      requestService.createRequest(requestData),
    onSuccess: (createdRequest) => {
      if (createdRequest.id) {
        queryClient.setQueryData(
          requestKeys.detail(createdRequest.id),
          createdRequest,
        );
      }
      invalidateRequestRelatedQueries(queryClient);
    },
  });
};

export const useBulkDeleteRequests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => requestService.bulkDeleteRequests(ids),
    onSuccess: (_, deletedIds) => {
      deletedIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: requestKeys.detail(id) });
      });
      invalidateRequestRelatedQueries(queryClient);
    },
  });
};

export const useUpdateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRequest }) =>
      requestService.updateRequest(id, data),
    onSuccess: (updatedRequest) => {
      queryClient.setQueryData(
        requestKeys.detail(updatedRequest.id || ""),
        updatedRequest,
      );
      invalidateRequestRelatedQueries(queryClient);
    },
  });
};

export const useMarkServiceRequestPaidOffline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      requestService.markServiceRequestPaidOffline(id),
    onSuccess: (_, requestId) => {
      queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      invalidateRequestRelatedQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: billingTransactionKeys.all });
    },
  });
};

export const useDeleteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requestService.deleteRequest(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: requestKeys.detail(deletedId) });
      invalidateRequestRelatedQueries(queryClient);
    },
  });
};

export const useRequestsWithContextFilters = (
  contextFilters: RequestFilters,
) => {
  return useQuery({
    queryKey: ["requests", "context", contextFilters],
    queryFn: () => requestService.getRequestsWithContextFilters(contextFilters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRequestsCount = (filters?: RequestFilters) => {
  return useQuery({
    queryKey: ["requests", "count", filters],
    queryFn: () => requestService.getRequestsCount(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
